import { createJsonAuthStorage, type AuthStorage, type AuthStorageKeys } from '@delta/auth';
import { axios } from '@delta/api-client';
import type { AxiosInstance } from 'axios';

interface TokenClaims {
  exp: number;
  kind: 'TENANT' | 'PLATFORM';
  sub: string;
  tid?: string;
  mid?: string;
  av: number;
  platform_role?: string;
}

function claims(token: string): TokenClaims | null {
  try {
    const part = token.split('.')[1];
    if (!part) return null;
    const value = JSON.parse(atob(part.replace(/-/g, '+').replace(/_/g, '/'))) as TokenClaims;
    if (!Number.isFinite(value.exp) || typeof value.sub !== 'string'
      || !['TENANT', 'PLATFORM'].includes(value.kind)) return null;
    return value;
  } catch { return null; }
}

export interface AdminSessionStorage<TUser> extends AuthStorage<TUser> {
  generation: () => number;
  renew: (token: string, generation: number, requestToken: string) => void;
}

export function createAdminSessionStorage<TUser>(storage: Storage, keys: AuthStorageKeys,
  onRenewed: () => void): AdminSessionStorage<TUser> {
  const stored = createJsonAuthStorage<TUser>(storage, keys);
  let generation = 0;
  return {
    ...stored,
    generation: () => generation,
    setToken: token => { generation++; stored.setToken(token); },
    clear: () => { generation++; stored.clear(); },
    renew: (token, started, requestToken) => {
      if (started !== generation) return;
      const currentToken = stored.getToken();
      if (!currentToken || !stored.getUser()) return;
      const previous = claims(currentToken);
      const source = claims(requestToken);
      const next = claims(token);
      if (!previous || !source || !next || next.exp <= previous.exp) return;
      // 只接收同一登录代际、身份和认证版本的更新；较早返回的令牌不能缩短有效期。
      for (const key of ['kind', 'sub', 'tid', 'mid', 'av', 'platform_role'] as const) {
        if (previous[key] !== source[key] || previous[key] !== next[key]) return;
      }
      stored.setToken(token);
      onRenewed();
    },
  };
}

export function installAdminSessionRenewal<TUser>(client: AxiosInstance, storage: AdminSessionStorage<TUser>,
  unauthorizedStatuses: readonly number[], onUnauthorized: () => void) {
  const requests = new WeakMap<object, { generation: number; token: string | null }>();
  client.interceptors.request.use(config => {
    requests.set(config, { generation: storage.generation(), token: storage.getToken() });
    return config;
  });
  client.interceptors.response.use(response => {
    const started = requests.get(response.config);
    const token: unknown = response.headers['x-delta-access-token'];
    if (response.status >= 200 && response.status < 300 && started?.token && typeof token === 'string') {
      storage.renew(token, started.generation, started.token);
    }
    return response;
  }, (error: unknown) => {
    if (axios.isAxiosError(error) && error.config && unauthorizedStatuses.includes(error.response?.status ?? 0)) {
      const started = requests.get(error.config);
      // 退出、切换账号或已续期后，旧请求的认证失败不能清除当前登录。
      if (started?.token && started.generation === storage.generation() && started.token === storage.getToken()) {
        storage.clear();
        onUnauthorized();
      }
    }
    return Promise.reject(error);
  });
}
